<template>
  <div class="self-study-page">
    <div class="study-container">
      <div class="page-header">
        <h1>个人中心</h1>
        <p class="subtitle">个性化自主学习，智能推荐，高效提升</p>
      </div>



      <!-- Tab切换 -->
      <div class="study-tabs">
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'answer' }"
          @click="activeTab = 'answer'"
        >错题记录</button>
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'exam' }"
          @click="activeTab = 'exam'"
        >考核记录</button>
      </div>

      <!-- 错题记录 -->
      <div class="tab-content" v-show="activeTab === 'answer'">
        <h2>错题记录</h2>
        <div class="question-history" v-if="paginatedQuestionHistory.length > 0">
          <div class="question-item" v-for="(q, index) in paginatedQuestionHistory" :key="index">
            <div class="question-header">
              <span class="question-type">{{ q.type }}</span>
              <span class="question-difficulty">{{ getDifficultyLabel(q.difficulty) }}</span>
              <span class="question-status wrong">
                ✗ 错误
              </span>
              <button class="remove-btn" @click="removeWrongQuestion(q)" title="移除此错题">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
            <div class="question-title">{{ q.title }}</div>
            <div v-if="q.titleImages && q.titleImages.length > 0" class="question-images">
              <img 
                v-for="(image, imgIndex) in q.titleImages" 
                :key="image.id || imgIndex"
                :src="image.imageUrl"
                :alt="`题目图片 ${imgIndex + 1}`"
                class="question-image"
                @click="openImagePreview(image.imageUrl)"
                style="cursor: pointer;"
              />
            </div>
            <div class="question-options" v-if="q.options">
              <div 
                class="option-row" 
                v-for="opt in q.options" 
                :key="opt.key"
                :class="{ 
                  'user-answer': q.userAnswer === opt.key,
                  'correct-answer': q.correctAnswer === opt.key,
                  'wrong-answer': q.userAnswer === opt.key && q.correctAnswer !== opt.key
                }"
              >
                <span class="opt-key">{{ opt.key }}.</span>
                <span class="opt-text" v-if="opt.text !== ''">{{ opt.text }}</span>
                  <img class="option-image" v-if="opt.imageUrl !== ''" :src=" opt.imageUrl " @click="openImagePreview(opt.imageUrl)" style="cursor: pointer;"/>
                <span class="opt-tag" v-if="q.userAnswer === opt.key && q.correctAnswer === opt.key">您的答案 ✓</span>
                <span class="opt-tag wrong" v-if="q.userAnswer === opt.key && q.correctAnswer !== opt.key">您的答案 ✗</span>
                <span class="opt-tag correct" v-if="q.correctAnswer === opt.key && q.userAnswer !== opt.key">正确答案</span>
              </div>
            </div>
            <div class="answer-compare" v-if="q.type === '单选题' || q.type === '多选题'">
              <span class="compare-item">您的答案：<strong :class="{ wrong: !q.isCorrect }">{{ q.userAnswer.join(', ') }}</strong></span>
              <span class="compare-item">正确答案：<strong class="correct">{{ q.correctAnswer.join(', ') }}</strong></span>
            </div>
            <div class="answer-text" v-else>
              <div class="text-row">您的答案：<span>{{ q.userAnswer }}</span></div>
              <div class="text-row correct-text">正确答案：<span>{{ q.correctAnswer }}</span></div>
            </div>
            <div class="question-explanation" v-if="q.explanation">
              <div class="explanation-title">
                <i class="fas fa-lightbulb"></i> 答案解析
              </div>
              <div class="explanation-content">{{ q.explanation }}</div>
            </div>
          </div>
        </div>
        <div class="empty-state" v-else>
          <p>🎉 太棒了！暂无错题记录</p>
        </div>
        <!-- 分页控件 -->
        <div class="pagination" v-if="totalAnswerPages > 1">
          <button class="page-btn" @click="prevAnswerPage" :disabled="answerPage === 1">上一页</button>
          <span class="page-info">{{ answerPage }} / {{ totalAnswerPages }}</span>
          <button class="page-btn" @click="nextAnswerPage" :disabled="answerPage === totalAnswerPages">下一页</button>
        </div>
      </div>

      <!-- 考核记录 -->
      <div class="tab-content" v-show="activeTab === 'exam'">
        <h2>考核记录</h2>
        <div class="record-list">
          <div class="record-item" v-for="(item, index) in paginatedExamRecords" :key="index">
            <div class="record-info">
              <h3>{{ item.title }}</h3>
              <p>{{ item.time }}</p>
            </div>
            <div class="record-stats">
              <div class="stat">
                <span class="stat-label">得分</span>
                <span class="stat-value">{{ item.score }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">用时</span>
              
                <span class="stat-value">{{ item.duration }}</span>
              </div>

            </div>
            <div class="record-actions">
              <button class="btn btn-danger remove-btn" @click="removeExamRecord(item)" title="删除此考核记录">
                <i class="fas fa-trash-alt"></i> 删除
              </button>
              <button class="btn btn-secondary" @click="showExamDetail(index)">查看详情</button>
            </div>
          </div>
        </div>
        <!-- 分页控件 -->
        <div class="pagination" v-if="totalExamPages > 1">
          <button class="page-btn" @click="prevExamPage" :disabled="examPage === 1">上一页</button>
          <span class="page-info">{{ examPage }} / {{ totalExamPages }}</span>
          <button class="page-btn" @click="nextExamPage" :disabled="examPage === totalExamPages">下一页</button>
        </div>
      </div>

      <!-- 试卷详情弹窗 -->
      <div class="modal-overlay" v-if="showModal" @click.self="closeModal">
        <div class="modal-container">
          <div class="modal-header">
            <h3>{{ currentExam ? currentExam.title : '' }} - 答题详情</h3>
            <button class="modal-close" @click="closeModal">&times;</button>
          </div>
          <div class="modal-body">
            <div class="exam-summary" v-if="currentExam">
              <div class="summary-item">
                <span class="label">得分：</span>
                <span class="value score">{{ currentExam.score }}分</span>
              </div>
              <div class="summary-item">
                <span class="label">用时：</span>
                <span class="value">{{ currentExam.duration }}</span>
              </div>
              <div class="summary-item">
                <span class="label">正确率：</span>
                <span class="value">{{ Math.round(getCorrectCount(currentExam) / currentExam.questions.filter(p => p.type === '单选题' || p.type === '多选题').length * 100) }}%</span>
              </div>
            </div>
            <div class="question-list">
              <div 
                class="question-item" 
                v-for="(q, qIndex) in (currentExam ? currentExam.questions.filter(p => p.type === '单选题' || p.type === '多选题') : [])" 
                :key="qIndex"
                :class="{ correct: q.isCorrect, wrong: !q.isCorrect }"
              >
                <div class="question-header">
                  <span class="question-index">第{{ qIndex + 1 }}题</span>
                  <span class="question-type">{{ q.type }}</span>
                  <span class="question-score">{{ q.score }}分</span>
                  <span class="question-status" :class="{ correct: q.isCorrect, wrong: !q.isCorrect }">
                    {{ q.isCorrect ? '✓ 正确' : '✗ 错误' }}
                  </span>
                </div>
                <div class="question-title">{{ q.title }}</div>
                <div class="question-options" v-if="q.options">
                  <div 
                    class="option-row" 
                    v-for="opt in q.options" 
                    :key="opt.key"
                    :class="{ 
                      'user-answer': q.userAnswer.includes(opt.key),
                      'correct-answer': q.correctAnswer.includes(opt.key),
                      'wrong-answer': q.userAnswer.includes(opt.key) && !q.correctAnswer.includes(opt.key)
                    }"
                  >
                    <span class="opt-key">{{ opt.key }}.</span>
                    <span class="opt-text" v-if="opt.text !== ''">{{ opt.text }}</span>
                  <img class="option-image" v-if="opt.imageUrl !== ''" :src=" opt.imageUrl " @click="openImagePreview(opt.imageUrl)" style="cursor: pointer;"/>
                    <span class="opt-tag" v-if="q.userAnswer.includes(opt.key) && q.correctAnswer.includes(opt.key)">您的答案 ✓</span>
                    <span class="opt-tag wrong" v-if="q.userAnswer.includes(opt.key) && !q.correctAnswer.includes(opt.key)">您的答案 ✗</span>
                    <span class="opt-tag correct" v-if="q.correctAnswer.includes(opt.key) && !q.userAnswer.includes(opt.key)">正确答案</span>
                  </div>
                </div>
                <div class="answer-compare" v-if="q.type === '单选题' || q.type === '多选题'">
                  <span class="compare-item">您的答案：<strong :class="{ wrong: !q.isCorrect }">{{ q.userAnswer.join(', ') || '未作答' }}</strong></span>
                  <span class="compare-item">正确答案：<strong class="correct">{{ q.correctAnswer.join(', ') }}</strong></span>
                </div>
                <div class="answer-text" v-else>
                  <div class="text-row">您的答案：<span>{{ q.userAnswer }}</span></div>
                  <div class="text-row correct-text">正确答案：<span>{{ q.correctAnswer }}</span></div>
                </div>
                <div class="question-explanation" v-if="q.explanation">
                  <div class="explanation-header">
                    <span class="explanation-icon">💡</span>
                    <span class="explanation-title">答案解析</span>
                  </div>
                  <p class="explanation-content">{{ q.explanation }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="closeModal">关闭</button>
          </div>
        </div>
      </div>

      <!-- 删除确认弹窗 -->
     <div class="modal-overlay" v-if="showDeleteConfirm" @click.self="cancelDeleteQuestion">
        <div class="modal-container delete-confirm-modal">
          <div class="modal-header">
            <h3>确认删除</h3>
            <button class="modal-close" @click="cancelDeleteQuestion">&times;</button>
          </div>
          <div class="modal-body">
            <div class="delete-confirm-content">
              <div class="delete-icon">⚠️</div>
              <p>{{ questionToDelete ? '确定要删除这道错题吗？' : '确定要删除这条考核记录吗？' }}</p>
              <p class="delete-warning">删除后将无法恢复</p>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cancelDeleteQuestion">取消</button>
            <button class="btn btn-danger" @click="questionToDelete ? confirmDeleteQuestion() : confirmDeleteExamRecord()">确认删除</button>
          </div>
        </div>
      </div> 

      <!-- 图片预览弹窗 -->
      <div class="image-modal" v-if="showImageModal">
        <div class="modal-content" @click="closeImagePreview">
          <button class="close-btn" @click.stop="closeImagePreview">×</button>
          <img :src="selectedImage" :alt="'预览图片'" class="preview-image" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getExamHistory, getExamDetail, getUselearnList, getUselearnDetail, getPersonalRanking, deleteUselearn, deleteExamRecord } from '@/api/droneApi';
import { baseURL } from '@/api/axiosConfig';
export default {
  name: 'SelfStudy',
  data() {
    return {
      activeTab: 'answer',
      showModal: false,
      currentExam: null,
      showDeleteConfirm: false,
      questionToDelete: null,
      examRecordToDelete: null,
      answerPage: 1,
      examPage: 1,
      pageSize: 5,
      learningPath: [],
      answerRecords: [],
      questionHistory: [], // 从接口获取错题记录
      examRecords: [],
      // 个人中心统计数据
      bestRank: 0,
      examCount: 0,
      averageScore: 0,
      // 考核记录分页信息
      totalExamCount: 0,
      totalExamPages: 1,
      // 图片预览相关状态
      showImageModal: false,
      selectedImage: ''
    }
  },
  created() {
    // 获取错题记录
    this.fetchQuestionHistory(this.answerPage);
    // 获取考核历史记录
    this.fetchExamHistory(this.examPage);
  },
  computed: {
    paginatedQuestionHistory() {
     // 只筛选错题，不做二次分页！接口已经分好页了
    return this.questionHistory.filter(q => !q.isCorrect)
    },
    paginatedExamRecords() {
      // 直接返回接口返回的分页数据，不需要前端切片
      return this.examRecords
    },
    // totalAnswerPages 现在直接使用 data 中的变量

    totalExamPages() {
      // 直接使用data中的totalExamPages变量，由接口返回
      return this.totalExamPages || 1
    }
  },
  methods: {
    // 获取错题记录
    async fetchQuestionHistory(pageNum = 1) {
      try {
        const res = await getUselearnList(pageNum, this.pageSize)
        console.log('错题列表接口返回:', res)
        if (res.data.code === 200 && res.data.data) {
          // 获取分页信息
          this.totalAnswerCount = res.data.data.total || 0
          this.totalAnswerPages = res.data.data.pages || 1
          
          // 获取list数组，处理嵌套数据结构
          const listData = Array.isArray(res.data.data.list) ? res.data.data.list : Array.isArray(res.data.data) ? res.data.data : []
          console.log('错题列表数据:', listData)
          
          // 转换接口返回的数据格式
          this.questionHistory = listData.map(item => {
            // 解析用户选择的选项
            const userAnswer = item.userAnswer ? item.userAnswer.split(',').map(id => {
              // 从选项中找到对应的key
              const option = item.options.find(opt => opt.id === id)
              return option ? option.optionLabel : id
            }) : []
            
            // 解析正确答案
            const correctAnswer = item.options ? item.options
              .filter(opt => opt.isCorrect === 1)
              .map(opt => opt.optionLabel) : []
            
            return {
              id: item.id,
              type: item.questionType === '0' ? '单选题' : '多选题',
              difficulty: item.difficulty || 1,
              title: item.questionContent || '暂无题目内容',
              titleImages: item.titleImages ? item.titleImages.map(img => ({
                ...img,
                imageUrl: baseURL + img.imageUrl
              })) : [],
              options: item.options ? item.options.map(opt => ({
                key: opt.optionLabel || '',
                text: opt.optionContent || '',
                imageUrl: opt.optionImages && opt.optionImages[0] && opt.optionImages[0].imageUrl 
  ? baseURL + opt.optionImages[0].imageUrl 
  : '',
              })) : [],
              userAnswer: userAnswer,
              correctAnswer: correctAnswer,
              isCorrect: JSON.stringify(userAnswer.sort()) === JSON.stringify(correctAnswer.sort()),
              explanation: item.analysis || '暂无解析'
            }
          })
          
          console.log('处理后的错题记录:', this.questionHistory)
        }
      } catch (error) {
        console.error('获取错题记录失败:', error)
      }
    },
    // 获取考核历史记录 （考核记录列表）
    async fetchExamHistory(pageNum = 1) {
      try {
        const res = await getExamHistory(pageNum, this.pageSize)
        console.log('考核历史接口返回:', res)
        if (res.data.code === 200 && res.data.data) {
          // 检查数据结构，处理两种可能的格式
          let listData = []
          if (Array.isArray(res.data.data)) {
            // 数据直接在data数组中
            listData = res.data.data
            // 由于接口返回的是完整数据，设置分页信息
            this.totalExamCount = listData.length
            this.totalExamPages = Math.ceil(listData.length / this.pageSize) || 1
            
            // 根据当前页码和每页大小进行前端分页
            const start = (pageNum - 1) * this.pageSize
            const end = start + this.pageSize
            listData = listData.slice(start, end)
          } else if (res.data.data.list) {
            // 数据在data.list中（分页接口格式）
            listData = Array.isArray(res.data.data.list) ? res.data.data.list : []
            this.totalExamCount = res.data.data.total || 0
            this.totalExamPages = res.data.data.pages || 1
          }
          
          console.log('考核历史数据:', listData)
          console.log('当前页码:', pageNum, '每页大小:', this.pageSize, '总页数:', this.totalExamPages)
          
          // 转换接口返回的数据格式
          this.examRecords = listData.map(item => {
            return {
              id: item.id, // 保存原始ID，用于获取详情
              title: item.paperName || (item.difficulty === 1 ? '小学组' : item.difficulty === 2 ? '初中组' : item.difficulty === 3 ? '高中组' : '未知组'),
              time: item.createTime,
              score: item.totalScore,
              duration: item.duration,
              rank: 0, // 接口返回数据中没有排名信息，暂时设为0
              questions: [] ,// 接口返回数据中没有题目详情，暂时设为空数组
            }
          })
          
          console.log('处理后的考核记录:', this.examRecords)
        }
      } catch (error) {
        console.error('获取考核历史失败:', error)
      }
    },
    prevAnswerPage() {
      if (this.answerPage > 1) {
        this.answerPage--
        this.fetchQuestionHistory(this.answerPage)
        this.scrollToTop()
      }
    },
    nextAnswerPage() {
      if (this.answerPage < this.totalAnswerPages) {
        this.answerPage++
        this.fetchQuestionHistory(this.answerPage)
        this.scrollToTop()
      }
    },
    prevExamPage() {
      if (this.examPage > 1) {
        this.examPage--
        this.fetchExamHistory(this.examPage)
        this.scrollToTop()
      }
    },
    nextExamPage() {
      if (this.examPage < this.totalExamPages) {
        this.examPage++
        this.fetchExamHistory(this.examPage)
        this.scrollToTop()
      }
    },
    
    // 滚动到顶部
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    showExamDetail(index) {
      const examRecord = this.examRecords[index]
      
      if (examRecord && examRecord.id) {
        this.fetchExamDetail(examRecord.id)
      }
    },
    //考核记录-查看详情
    async fetchExamDetail(recordId) {
      try {
        const res = await getExamDetail(recordId)
        if (res.data.code === 200 && res.data.data) {
          const detailData = res.data.data
          
          // 转换接口返回的数据格式
          const questions = detailData.details.map(item => {
            const questionType = item.questionType === '0' ? '单选题' : item.questionType === '1' ? '多选题' : '判断题'
            
            return {
              type: questionType,
              title: item.questionTitle,
              score: item.score,
              userAnswer: item.userAnswer ? item.userAnswer.split(',') : [],
              correctAnswer: item.correctAnswers ? item.correctAnswers.split(',') : [],
              isCorrect: item.isCorrect === 1,
              explanation: item.analysis || '',
              options: item.options ? item.options.map(opt => ({
                key: opt.optionLabel,
                text: opt.optionContent,
                imageUrl: opt.optionImages && opt.optionImages[0] && opt.optionImages[0].imageUrl 
  ? baseURL + opt.optionImages[0].imageUrl 
  : '',
                isCorrect: opt.isCorrect === 1
              })) : []
            }
          })
          
          this.currentExam = {
            title: detailData.paperName,
            time: detailData.createTime,
            score: detailData.totalScore,
            duration: detailData.duration,
            rank: 0,
            questions: questions
          }
          
          this.showModal = true
        }
      } catch (error) {
        console.error('获取考核详情失败:', error)
        alert('获取考核详情失败，请稍后重试')
      }
    },
    closeModal() {
      this.showModal = false
      this.currentExam = null
    },
    removeWrongQuestion(question) {
      this.questionToDelete = question
      this.showDeleteConfirm = true
    },
    removeExamRecord(examRecord) {
      this.examRecordToDelete = examRecord
      this.showDeleteConfirm = true
    },
    async confirmDeleteQuestion() {
      if (this.questionToDelete) {
        try {
          // 调用删除错题接口
          const res = await deleteUselearn(this.questionToDelete.id)
          if (res.data.code === 200) {
            // 删除成功，重新获取当前页的错题列表
            await this.fetchQuestionHistory(this.answerPage)
          } else {
            console.error('删除失败：' + res.data.msg)
          }
        } catch (error) {
          console.error('删除错题失败:', error)
        } finally {
          this.showDeleteConfirm = false
          this.questionToDelete = null
        }
      }
    },
    async confirmDeleteExamRecord() {
      if (this.examRecordToDelete) {
        try {
          // 调用删除考核记录接口
          const res = await deleteExamRecord(this.examRecordToDelete.id)
          if (res.data.code === 200) {
            // 删除成功，重新获取当前页的考核记录
            await this.fetchExamHistory(this.examPage)
          } else {
            console.error('删除失败：' + res.data.msg)
          }
        } catch (error) {
          console.error('删除考核记录失败:', error)
        } finally {
          this.showDeleteConfirm = false
          this.examRecordToDelete = null
        }
      }
    },
    cancelDeleteQuestion() {
      this.showDeleteConfirm = false
      this.questionToDelete = null
      this.examRecordToDelete = null
    },
    getCorrectCount(exam) {
      return exam.questions.filter(q => (q.type === '单选题' || q.type === '多选题') && q.isCorrect).length
    },
    // 获取个人中心统计数据
    async fetchPersonalRanking() {
      try {
        const res = await getPersonalRanking()
        console.log('个人中心统计数据:', res)
        if (res.data.code === 200 && res.data.data) {
          this.bestRank = res.data.data.bestRank || 0
          this.examCount = res.data.data.examCount || 0
          this.averageScore = res.data.data.avgScore || 0
        }
      } catch (error) {
        console.error('获取个人中心统计数据失败:', error)
      }
    },
    
    // 打开图片预览
    openImagePreview(imageUrl) {
      this.selectedImage = imageUrl
      this.showImageModal = true
    },
    
    // 关闭图片预览
    closeImagePreview() {
      this.showImageModal = false
      this.selectedImage = ''
    },
    // 获取难度标签
    getDifficultyLabel(difficulty) {
      const labels = {
        1: '小学',
        2: '初中',
        3: '高中'
      }
      return labels[difficulty] || '小学'
    }
  },
  mounted() {
    // 获取个人中心统计数据
    this.fetchPersonalRanking()
    // 组件挂载时获取考核历史记录
    this.fetchExamHistory(this.examPage)
  }
}
</script>

<style scoped>
.self-study-page {
  padding: 20px;
  min-height: calc(100vh - 200px);
}

.study-container {
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 32px;
  color: #1a5f7a;
  margin-bottom: 10px;
}

.subtitle {
  color: #666;
  font-size: 16px;
  margin: 0;
}

/* 学习统计 */
.study-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
}

.stat-card.inline {
  background: white;
  border-radius: 12px;
  padding: 15px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  min-width: 120px;
}

.stat-card.inline:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
}

.stat-card.inline .stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.stat-card.inline .stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #1a5f7a;
}

/* Tab切换 */
.study-tabs {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 25px;
  border-bottom: 2px solid #eee;
  padding-bottom: 0;
}

.tab-btn {
  padding: 12px 24px;
  background: none;
  border: none;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #1a5f7a;
}

.tab-btn.active {
  color: #1a5f7a;
  font-weight: bold;
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #1a5f7a;
  border-radius: 3px 3px 0 0;
}

/* Tab内容 */
.tab-content {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.tab-content h2 {
  color: #1a5f7a;
  font-size: 22px;
  margin: 0 0 25px;
}

/* 学习路径 */
.learning-path {
  position: relative;
  padding-left: 30px;
}

.learning-path::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #1a5f7a, #e0e0e0);
}

.path-item {
  position: relative;
  padding: 0 0 30px 30px;
}

.path-item:last-child {
  padding-bottom: 0;
}

.path-marker {
  position: absolute;
  left: -30px;
  top: 0;
  width: 32px;
  height: 32px;
  background: #e0e0e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #999;
  z-index: 1;
  border: 3px solid #fff;
}

.path-item.completed .path-marker {
  background: #27ae60;
  color: white;
}

.path-item.current .path-marker {
  background: #1a5f7a;
  color: white;
}

.path-content {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  transition: all 0.3s;
}

.path-item.current .path-content {
  background: #e8f4f8;
  border: 2px solid #1a5f7a;
}

.path-item.completed .path-content {
  background: #f0fff4;
}

.path-content h3 {
  margin: 0 0 8px;
  color: #333;
  font-size: 18px;
}

.path-content p {
  margin: 0 0 15px;
  color: #666;
  font-size: 14px;
}

.path-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1a5f7a, #2d8fb3);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.path-item.completed .progress-fill {
  background: #27ae60;
}

.progress-text {
  font-size: 14px;
  font-weight: bold;
  color: #1a5f7a;
  min-width: 40px;
}

.path-item.completed .progress-text {
  color: #27ae60;
}

/* 按钮 */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #1a5f7a, #2d8fb3);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #155269, #2579a3);
}

.btn-secondary {
  background: #e0e0e0;
  color: #333;
}

.btn-secondary:hover {
  background: #d0d0d0;
}

.btn-sm {
  padding: 8px 16px;
  font-size: 13px;
}

/* 题目历史 */
.question-history {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-item {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid #e0e0e0;
  transition: all 0.3s;
}

.question-item.correct {
  border-left-color: #27ae60;
  background: #f0fff4;
}

.question-item.wrong {
  border-left-color: #e74c3c;
  background: #fff5f5;
}

.question-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.question-index {
  font-weight: bold;
  color: #666;
  font-size: 14px;
}

.question-type {
  padding: 4px 10px;
  background: #e8f4f8;
  color: #1a5f7a;
  border-radius: 4px;
  font-size: 12px;
}

.question-difficulty {
  padding: 4px 10px;
  background: #e8f0e8;
  color: #27ae60;
  border-radius: 4px;
  font-size: 12px;
}

.question-status {
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.question-status.correct {
  background: #27ae60;
  color: white;
}

.question-status.wrong {
  background: #e74c3c;
  color: white;
}

.question-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.6;
}

.question-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-row {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background: white;
  border-radius: 8px;
  border: 2px solid transparent;
  gap: 10px;
}

.option-row.user-answer {
  border-color: #1a5f7a;
  background: #e8f4f8;
}

.option-row.correct-answer {
  border-color: #27ae60;
  background: #e8f8f0;
}

.option-row.wrong-answer {
  border-color: #e74c3c;
  background: #ffeaea;
}

.opt-key {
  font-weight: bold;
  color: #666;
  min-width: 25px;
}

.opt-text {
  flex: 1;
  color: #333;
}

.opt-tag {
  padding: 3px 8px;
  background: #1a5f7a;
  color: white;
  border-radius: 4px;
  font-size: 11px;
}

.opt-tag.correct {
  background: #27ae60;
}

.opt-tag.wrong {
  background: #e74c3c;
}

.answer-compare {
  display: flex;
  gap: 30px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.compare-item {
  font-size: 14px;
  color: #666;
}

.compare-item strong {
  color: #333;
}

.compare-item strong.wrong {
  color: #e74c3c;
}

.compare-item strong.correct {
  color: #27ae60;
}

.answer-text {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.text-row {
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}

.text-row span {
  color: #333;
  display: block;
  margin-top: 5px;
  padding: 10px;
  background: white;
  border-radius: 6px;
}

.text-row.correct-text span {
  color: #27ae60;
}

/* 解析样式 */
.question-explanation {
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #fff9e6, #fffbf0);
  border-radius: 8px;
  border-left: 4px solid #f0ad4e;
}

.explanation-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.explanation-icon {
  font-size: 18px;
}

.explanation-title {
  font-size: 14px;
  font-weight: bold;
  color: #856404;
}

.explanation-content {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  margin: 0;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #1a5f7a;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.3s;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 25px;
}

.exam-summary {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding: 15px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 25px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-item .label {
  color: #666;
  font-size: 14px;
}

.summary-item .value {
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

.summary-item .value.score {
  color: #1a5f7a;
  font-size: 20px;
}

.question-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.modal-footer {
  padding: 15px 25px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

/* 删除确认弹窗样式 */
.delete-confirm-modal {
  max-width: 400px;
}

.delete-confirm-content {
  text-align: center;
  padding: 20px;
}

.delete-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.delete-confirm-content p {
  font-size: 16px;
  color: #333;
  margin: 10px 0;
}

.delete-warning {
  color: #dc3545;
  font-weight: 600;
  font-size: 14px;
}

.btn-danger {
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-danger:hover {
  background: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

/* 记录列表 */
.record-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.record-item {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s;
}

.record-item:hover {
  background: #f0f4f8;
  transform: translateX(5px);
}

.record-actions {
  display: flex;
  gap: 10px;
}

.record-info {
  flex: 1;
}

.record-info h3 {
  margin: 0 0 5px;
  color: #333;
  font-size: 16px;
}

.record-info p {
  margin: 0;
  color: #999;
  font-size: 13px;
}

.record-stats {
  display: flex;
  gap: 30px;
  margin: 0 30px;
}

.record-stats .stat {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #999;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

/* 响应式 */
@media (max-width: 768px) {
  .study-stats {
    flex-wrap: wrap;
    gap: 15px;
  }
  
  .stat-card.inline {
    min-width: 100px;
    padding: 12px 20px;
  }
  
  .study-tabs {
    flex-wrap: wrap;
  }
  
  .tab-btn {
    padding: 10px 16px;
    font-size: 14px;
  }
  
  .record-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .record-stats {
    margin: 0;
    gap: 20px;
  }
  
  .learning-path {
    padding-left: 20px;
  }
  
  .learning-path::before {
    left: 10px;
  }
  
  .path-marker {
    left: -20px;
    width: 26px;
    height: 26px;
    font-size: 12px;
  }
  
  .path-item {
    padding-left: 20px;
  }
}

@media (max-width: 480px) {
  .study-stats {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  
  .stat-card {
    padding: 15px;
  }
  
  .stat-icon {
    font-size: 28px;
  }
  
  .stat-info .stat-value {
    font-size: 20px;
  }
}

/* 分页控件 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  padding: 15px 0;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #1a5f7a;
  background: white;
  color: #1a5f7a;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.page-btn:hover:not(:disabled) {
  background: #1a5f7a;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
  min-width: 60px;
  text-align: center;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
}

.empty-state p {
  margin: 0;
  line-height: 1.8;
}

/* 答案解析 */
.question-explanation {
  margin-top: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.explanation-title {
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.explanation-title i {
  color: #ffc107;
}

.explanation-content {
  font-size: 14px;
  color: #664d03;
  line-height: 1.6;
}

/* 移除按钮 */
.remove-btn {
  margin-left: auto;
  padding: 4px 10px;
  border: 1px solid #dc3545;
  background: white;
  color: #dc3545;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.remove-btn:hover {
  background: #dc3545;
  color: white;
}

.remove-btn i {
  font-size: 12px;
}

/* 选项图片样式 */
.option-image {
  max-width: 80%;
  max-height: 150px;
  margin: 8px 0;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  object-fit: contain;
}

/* 题目图片样式 */
.question-images {
  margin: 15px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.question-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: transform 0.2s;
}

.question-image:hover {
  transform: scale(1.02);
}

/* 图片预览模态框 */
.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.image-modal .modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: transparent;
  border-radius: 0;
  padding: 0;
  animation: slideUp 0.3s ease;
}

.image-modal .close-btn {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s;
}

.image-modal .close-btn:hover {
  background: #f0f0f0;
  transform: scale(1.1);
}

.preview-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .image-modal .close-btn {
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    font-size: 20px;
  }
}
</style>
